import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EditProviderProfile.css';
import StepNavigation from './EditionComponents/StepNavigation';
import ProgressIndicator from './EditionComponents/ProgressIndicator';
import BasicInfoStep from './EditionComponents/BasicInfoStep';
import ContactStep from './EditionComponents/ContactStep';
import CategoriesStep from './EditionComponents/CategoriesStep';
import MediaStep from './EditionComponents/MediaStep';
import PreviewStep from './EditionComponents/PreviewStep';
import SignupDocumentsStep from './EditionComponents/SignupDocumentsStep.jsx';
import RegistrationSuccess from '../RegistrationSuccess/RegistrationSuccess';
import { registerProductsProvider, registerServiceProvider } from '../../services/providersApi';
import { usernameAvailable } from '../../services/providersApi';
import { getUserData, saveUserData } from '../../utils/auth';
import { useCategories } from '../../utils/CategoriesContext';
import { useAuth } from '../../utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import Spinner from '../Spinner/Spinner';
import { prepareProviderDataForBackend } from '../../utils/migrationHelpers'; // 🎯 Helper de migración

const RegisterProviderProfile = ({ providerType }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const { getCategoriesByType, loadingCategories } = useCategories();
    const { setUserData } = useAuth();

    const [isScrolling, setIsScrolling] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const categories = getCategoriesByType(providerType === 'service' ? 'service' : 'products');
    const loading = loadingCategories;
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [formData, setFormData] = useState({
        type: providerType, // 'service' | 'products'
        username: '',
        name: '',
        description: '',
        phone: '',
        timetable: '',
        links: [],
        locations: [],
        branches: [], // products only
        categories: [],
        subcategories: [],
        keywords: [],
        certificates: [], // service only
        photo: '',
        workPhotos: [],
        // documents (service)
        document: '',
        documentName: '',
        documentPhoto: '',
        // products only
        cuit: '',
    });

    // Steps (conditional)
    const STEPS = useMemo(() => {
        const base = [
            { id: 'basic', title: 'Información Básica' },
        ];
        if (providerType === 'service') {
            base.push({ id: 'documents', title: 'Documentación' });
        }
        base.push(
            { id: 'contact', title: 'Contacto' },
            { id: 'categories', title: 'Categorías' },
            { id: 'media', title: 'Fotos' },
            { id: 'preview', title: 'Vista Previa' }
        );
        return base;
    }, [providerType]);

    // Theme color like editor
    useEffect(() => {
        const root = document.documentElement;
        if (providerType === 'products') {
            root.style.setProperty('--primary-color', '#2196f3');
            root.style.setProperty('--primary-hover', '#1976d2');
        } else {
            root.style.setProperty('--primary-color', '#f44336');
            root.style.setProperty('--primary-hover', '#d32f2f');
        }
        return () => {
            root.style.setProperty('--primary-color', '#bb86fc');
            root.style.setProperty('--primary-hover', '#9c6dfd');
        };
    }, [providerType]);

    // Guard clause: if already provider, redirect
    useEffect(() => {
        const userData = getUserData();
        if (userData && (userData.isProvider === true || userData.isProvider === 'true')) {
            toast.error('Ups! Ya te registraste como proveedor, no podés acceder a esta sección.');
            navigate('/');
        }
    }, [navigate]);

    // Validation (moved before usage)
    const validateField = useCallback((field, value) => {
        const fieldErrors = {};
        switch (field) {
            case 'username': {
                if (!value || value.trim() === '') fieldErrors.username = 'El nombre de usuario es obligatorio';
                else if (!/^[a-zA-Z-_]+$/.test(value)) fieldErrors.username = 'El nombre de usuario solo puede contener letras y guiones';
                break;
            }
            case 'name': {
                if (!value || value.trim() === '') fieldErrors.name = 'El nombre es requerido';
                else if ((value || '').length > 100) fieldErrors.name = 'El nombre no puede exceder 100 caracteres';
                break;
            }
            case 'description': {
                if (!value || value.trim() === '') fieldErrors.description = 'La descripción es requerida';
                else if ((value || '').length > 1000) fieldErrors.description = 'La descripción no puede exceder 1000 caracteres';
                break;
            }
            case 'phone': {
                if (!value || value.trim() === '') fieldErrors.phone = 'El teléfono es requerido';
                else if (!/^\d+$/.test(value)) fieldErrors.phone = 'El teléfono debe contener solo números';
                else if (value.length < 8 || value.length > 15) fieldErrors.phone = 'El teléfono debe tener entre 8 y 15 dígitos';
                break;
            }
            case 'categories': {
                if (!value || value.length === 0) fieldErrors.categories = 'Debes seleccionar al menos una categoría';
                break;
            }
            case 'locations': {
                if (!value || value.length === 0) fieldErrors.locations = 'Debes seleccionar al menos una ubicación';
                break;
            }
            case 'photo': {
                if (!value || String(value).trim() === '') fieldErrors.photo = 'La foto de perfil es requerida';
                break;
            }
            case 'document': {
                if (providerType === 'service') {
                    // Solo validar formato si hay valor
                    if (value && String(value).trim() !== '') {
                        if (!/^\d{8}$/.test(String(value))) {
                            fieldErrors.document = 'El DNI debe contener exactamente 8 dígitos';
                        }
                    }
                    // No validar requerimiento aquí - se maneja en la validación final
                }
                break;
            }
            case 'documentName': {
                if (providerType === 'service') {
                    if (!value || String(value).trim() === '') fieldErrors.documentName = 'El nombre completo es requerido';
                    else if (!/^[a-zA-ZÁÉÍÓÚáéíóúñÑ\s]+$/.test(String(value))) fieldErrors.documentName = 'Solo letras y espacios';
                }
                break;
            }
            /*
            case 'documentPhoto': {
              if (providerType === 'service') {
                if (!value || String(value).trim() === '') fieldErrors.documentPhoto = 'La foto del documento es requerida';
              }
              break;
            }
            */
            case 'cuit': {
                if (providerType === 'products') {
                    if (!value || String(value).trim() === '') fieldErrors.cuit = 'El CUIT/CUIL es requerido';
                    else if (!/^\d{11}$/.test(String(value))) fieldErrors.cuit = 'Debe contener exactamente 11 dígitos';
                } else if (providerType === 'service') {
                    // Para servicios, solo validar formato si hay valor
                    if (value && String(value).trim() !== '') {
                        if (!/^\d{11}$/.test(String(value))) {
                            fieldErrors.cuit = 'El CUIT debe contener exactamente 11 dígitos';
                        }
                    }
                    // No validar requerimiento aquí - se maneja en la validación final
                }
                break;
            }
            default:
                break;
        }

        setErrors(prev => {
            const next = { ...prev };
            if (Object.keys(fieldErrors).length === 0) delete next[field];
            else next[field] = fieldErrors[field];
            return next;
        });
        return Object.keys(fieldErrors).length === 0;
    }, [providerType]);

    // updateFormData helper (now after validateField)
    const updateFormData = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasUnsavedChanges(true);
        // defer validation for smoother UX
        setTimeout(() => { validateField(field, value); }, 0);
    }, [validateField]);

    const setFormDataCompat = useCallback((updater) => {
        if (typeof updater === 'function') {
            setFormData(prev => {
                const next = updater(prev);
                setHasUnsavedChanges(true);
                return next;
            });
        } else {
            setFormData(updater);
            setHasUnsavedChanges(true);
        }
    }, []);

    // Username availability check on step advance
    const checkUsernameAvailability = useCallback(async () => {
        try {
            const res = await usernameAvailable(formData.username);
            return res === 'Username available';
        } catch (e) {
            if (e?.response?.status === 409) {
                setErrors(prev => ({ ...prev, username: 'Este nombre de usuario ya existe' }));
            } else {
                toast.error('No se pudo verificar el nombre de usuario');
            }
            return false;
        }
    }, [formData.username]);

    // Progress computation including signup-only fields
    const profileProgress = useMemo(() => {
        const required = ['username', 'name', 'description', 'phone', 'categories', 'locations', 'photo'];
        if (providerType === 'products') required.push('cuit');
        if (providerType === 'service') {
            required.push('documentName'/*, 'documentPhoto'*/);
            // Para servicios, SIEMPRE se requiere DNI O CUIT (agregar campo virtual desde el inicio)
            required.push('document_or_cuit');
        }

        const done = required.filter(field => {
            if (field === 'document_or_cuit') {
                // Campo virtual: considerar completado si tiene DNI o CUIT
                const hasDocument = formData.document && String(formData.document).trim() !== '';
                const hasCuit = formData.cuit && String(formData.cuit).trim() !== '';
                return hasDocument || hasCuit;
            }

            const v = formData[field];
            if (Array.isArray(v)) return v.length > 0;
            return v && String(v).trim() !== '';
        });
        return Math.round((done.length / required.length) * 100);
    }, [formData, providerType]);

    const validateCurrentStep = useCallback(async () => {
        const step = STEPS[currentStep];
        if (!step) return false;

        switch (step.id) {
            case 'basic': {
                const ok = [
                    validateField('username', formData.username),
                    validateField('name', formData.name),
                    validateField('description', formData.description),
                ].every(Boolean);
                if (!ok) return false;
                // Check username availability
                return await checkUsernameAvailability();
            }
            case 'documents': {
                if (providerType !== 'service') return true;
                const documentNameValid = validateField('documentName', formData.documentName);

                // Validar que tenga DNI o CUIT (al menos uno de los dos)
                const hasDocument = formData.document && String(formData.document).trim() !== '';
                const hasCuit = formData.cuit && String(formData.cuit).trim() !== '';

                if (!hasDocument && !hasCuit) {
                    setErrors(prev => ({ ...prev, document: 'Debes ingresar tu DNI o CUIT' }));
                    return false;
                }

                // Validar el formato del campo que esté completo
                let documentValid = true;
                if (hasDocument) {
                    documentValid = validateField('document', formData.document);
                }
                if (hasCuit) {
                    documentValid = documentValid && validateField('cuit', formData.cuit);
                }

                return [documentNameValid, documentValid].every(Boolean);
            }
            case 'contact': {
                const baseOk = [
                    validateField('phone', formData.phone),
                    validateField('locations', formData.locations),
                ].every(Boolean);
                if (!baseOk) return false;
                if (providerType === 'products') return validateField('cuit', formData.cuit);
                return true;
            }
            case 'categories':
                return validateField('categories', formData.categories);
            case 'media':
                return validateField('photo', formData.photo);
            default:
                return true;
        }
    }, [STEPS, currentStep, formData, providerType, validateField, checkUsernameAvailability]);

    // Helper para breadcrumbs (acciones clave del usuario)
    const addBreadcrumb = useCallback((event, data = {}) => {
        try {
            window.__BREADCRUMBS = window.__BREADCRUMBS || [];
            window.__BREADCRUMBS.push({ t: Date.now(), event, ...data });
            if (window.__BREADCRUMBS.length > 50) window.__BREADCRUMBS.shift();
        } catch (_) {}
    }, []);

    // Submit handler
    const handleFinalSave = useCallback(async () => {
        addBreadcrumb('final_save_start');

        // Validaciones finales inteligentes - solo validar campos relevantes
        const fieldsToValidate = ['username', 'name', 'description', 'phone', 'categories', 'locations', 'photo'];

        if (providerType === 'service') {
            fieldsToValidate.push('documentName');
            // Para servicios: validar solo el campo de documento que tiene valor
            const hasDocument = formData.document && String(formData.document).trim() !== '';
            const hasCuit = formData.cuit && String(formData.cuit).trim() !== '';

            if (hasDocument) {
                fieldsToValidate.push('document');
            } else if (hasCuit) {
                fieldsToValidate.push('cuit');
            } else {
                // Si no tiene ninguno, mostrar error
                toast.error('Debes completar tu DNI o CUIT');
                addBreadcrumb('final_save_abort_missing_document');
                return;
            }
        }

        if (providerType === 'products') {
            fieldsToValidate.push('cuit');
        }

        // Realizar validaciones sincrónicas directas
        const validationErrors = {};

        // Validar campos básicos
        for (const field of fieldsToValidate) {
            const value = formData[field];

            // Validaciones específicas por campo
            switch (field) {
                case 'username':
                    if (!value || value.trim() === '') validationErrors.username = 'El nombre de usuario es obligatorio';
                    else if (!/^[a-zA-Z-_]+$/.test(value)) validationErrors.username = 'El nombre de usuario solo puede contener letras y guiones';
                    break;
                case 'name':
                    if (!value || value.trim() === '') validationErrors.name = 'El nombre es requerido';
                    break;
                case 'description':
                    if (!value || value.trim() === '') validationErrors.description = 'La descripción es requerida';
                    break;
                case 'phone':
                    if (!value || value.trim() === '') validationErrors.phone = 'El teléfono es requerido';
                    else if (!/^\d+$/.test(value)) validationErrors.phone = 'El teléfono debe contener solo números';
                    break;
                case 'categories':
                    if (!value || value.length === 0) validationErrors.categories = 'Debes seleccionar al menos una categoría';
                    break;
                case 'locations':
                    if (!value || value.length === 0) validationErrors.locations = 'Debes seleccionar al menos una ubicación';
                    break;
                case 'photo':
                    if (!value || String(value).trim() === '') validationErrors.photo = 'La foto de perfil es requerida';
                    break;
                case 'document':
                    if (value && String(value).trim() !== '' && !/^\d{8}$/.test(String(value))) {
                        validationErrors.document = 'El DNI debe contener exactamente 8 dígitos';
                    }
                    break;
                case 'cuit':
                    if (value && String(value).trim() !== '' && !/^\d{11}$/.test(String(value))) {
                        validationErrors.cuit = 'El CUIT debe contener exactamente 11 dígitos';
                    }
                    break;
                case 'documentName':
                    if (!value || value.trim() === '') validationErrors.documentName = 'El nombre completo es requerido';
                    break;
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Por favor corrige los errores antes de continuar');
            addBreadcrumb('final_save_abort_validation_errors', { errors: Object.keys(validationErrors) });
            return;
        }

        // Si llegamos aquí, proceder con el guardado
        setSaving(true);
        try {
            let payload = { ...formData };
            // Ensure arrays are clean
            payload.keywords = (payload.keywords || []).filter(k => (k || '').trim());
            payload.branches = (payload.branches || []).filter(b => (b || '').trim());

            // 🎯 Preparar datos para backend (maneja migración)
            payload = prepareProviderDataForBackend(payload, false, categories); // false = creating new provider, categories = available categories

            if (providerType === 'service') {
                await registerServiceProvider(payload);
            } else {
                await registerProductsProvider(payload);
            }

            // Update local user data (cache + AuthContext state)
            const user = getUserData() || {};
            user.isProvider = true;
            user.providerRef = payload.username;
            user.providerType = providerType;
            saveUserData(user);
            setUserData(user);

            toast.success('¡Perfil creado exitosamente!');
            addBreadcrumb('final_save_success');
            setShowSuccessModal(true);
        } catch (e) {
            if (e?.response?.status === 409) {
                toast.error('El nombre de usuario ya existe');
                setCurrentStep(0);
                setErrors(prev => ({ ...prev, username: 'Este nombre de usuario ya existe' }));
                addBreadcrumb('final_save_conflict_username');
            } else {
                toast.error('Error al crear el perfil');
                console.error('Signup error:', e);
                addBreadcrumb('final_save_error', { message: e?.message });
            }
        } finally {
            setSaving(false);
        }
    }, [errors, formData, providerType, validateField, addBreadcrumb, categories, setUserData]);

    const handleCloseSuccessModal = useCallback(() => {
        setShowSuccessModal(false);
        navigate('/');
    }, [navigate]);

    // Función para hacer scroll al primer campo con error
    const scrollToFirstError = useCallback(() => {
        // Activar el bloqueo de interacción
        setIsScrolling(true);

        // Añadir un pequeño delay para asegurar que el DOM está actualizado
        setTimeout(() => {
            try {
                const errorElement = document.querySelector('.form-input.error, .form-textarea.error, .react-select__control--is-focused');
                // Verificación más robusta de la existencia del elemento
                if (errorElement && document.body && document.body.contains(errorElement) && errorElement.scrollIntoView) {
                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });

                    // Desactivar el bloqueo después de la animación de scroll (300ms)
                    setTimeout(() => {
                        setIsScrolling(false);
                    }, 300);
                } else {
                    // Si no hay elemento, liberar el bloqueo inmediatamente
                    setIsScrolling(false);
                }
            } catch (error) {
                console.warn("Error al hacer scroll a elemento:", error.message);
                // En caso de error, asegurar que se libere el bloqueo
                setIsScrolling(false);
            }
        }, 100);
    }, []);

    // Función para hacer scroll al primer campo del paso actual
    const scrollToFirstField = useCallback(() => {
        // Activar el bloqueo de interacción
        setIsScrolling(true);

        // Añadir un pequeño delay para asegurar que el DOM está actualizado
        setTimeout(() => {
            try {
                // Hacer scroll hasta arriba de todo de forma simple
                if (typeof window !== 'undefined' && window.scrollTo) {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });

                    // Desactivar el bloqueo después de la animación de scroll (300ms)
                    setTimeout(() => {
                        setIsScrolling(false);
                    }, 300);
                } else {
                    // Si no se puede hacer scroll, liberar el bloqueo inmediatamente
                    setIsScrolling(false);
                }
            } catch (error) {
                console.warn("Error al hacer scroll:", error.message);
                // En caso de error, asegurar que se libere el bloqueo
                setIsScrolling(false);
            }
        }, 100);
    }, []);

    // Efecto para scroll automático cuando cambia el paso
    useEffect(() => {
        addBreadcrumb('step_change', { stepIndex: currentStep, stepId: STEPS[currentStep]?.id });
        scrollToFirstField();
    }, [currentStep, scrollToFirstField, STEPS, addBreadcrumb]);

    // Efecto para scroll automático cuando aparecen errores
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            scrollToFirstError();
        }
    }, [errors, scrollToFirstError]);

    // Render step
    const renderCurrentStep = useMemo(() => {
        // Si está cargando, no renderizar ningún paso
        if (loading) return null;

        const stepProps = {
            formData,
            updateFormData,
            setFormData: setFormDataCompat,
            errors,
            categories,
        };

        const step = STEPS[currentStep]?.id;
        let stepComponent = null;

        try {
            switch (step) {
                case 'basic':
                    stepComponent = <BasicInfoStep key="basic-step" {...stepProps} showUsername />;
                    break;
                case 'documents':
                    stepComponent = <SignupDocumentsStep key="documents-step" {...stepProps} />;
                    break;
                case 'contact':
                    stepComponent = <ContactStep key="contact-step" {...stepProps} showCuit={providerType === 'products'} />;
                    break;
                case 'categories':
                    stepComponent = <CategoriesStep key="categories-step" {...stepProps} />;
                    break;
                case 'media':
                    stepComponent = <MediaStep key="media-step" {...stepProps} />;
                    break;
                case 'preview':
                    stepComponent = <PreviewStep key="preview-step" {...stepProps} onSave={handleFinalSave} saving={saving} />;
                    break;
                default:
                    stepComponent = null;
            }
            return stepComponent;
        } catch (error) {
            console.error("Error al renderizar el paso:", error);
            return <div className="error-message">Ocurrió un error al cargar este paso. Por favor intenta recargar la página.</div>;
        }
    }, [STEPS, currentStep, formData, updateFormData, setFormDataCompat, errors, categories, providerType, handleFinalSave, saving, loading]);

    // Modificar los manejadores de eventos para que no respondan durante el scroll
    const handlePrevStep = useCallback(() => {
        if (isScrolling) { addBreadcrumb('click_prev_blocked', { reason: 'isScrolling' }); return; }
        if (currentStep > 0) {
            addBreadcrumb('click_prev', { from: currentStep });
            setCurrentStep(s => s - 1);
        }
    }, [currentStep, isScrolling, addBreadcrumb]);

    const handleNextStep = useCallback(async () => {
        if (isScrolling) { addBreadcrumb('click_next_blocked', { reason: 'isScrolling' }); return; }
        addBreadcrumb('click_next_attempt', { from: currentStep, stepId: STEPS[currentStep]?.id });
        const ok = await validateCurrentStep();
        addBreadcrumb('click_next_result', { ok });
        if (ok && currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
    }, [validateCurrentStep, currentStep, STEPS, isScrolling, addBreadcrumb]);

    // Modificar el handler de cambio de paso directo
    const handleStepClick = useCallback((stepIndex) => {
        if (isScrolling) return; // No permitir cambio de paso durante el scroll
        setCurrentStep(stepIndex);
    }, [isScrolling]);

    // Sanitizador de anuncios: desactivado temporalmente a pedido para probar solo con la configuración de AdSense y sin scripts en index.html

    useEffect(() => {
        const SANITIZER_ENABLED = process.env.REACT_APP_AD_SANITIZER !== 'off';
        if (!SANITIZER_ENABLED) return;

        const root = containerRef.current;
        if (!root) return;

        // Si estamos guardando o mostrando el modal de éxito, desactivar el observer para evitar interferencias
        if (saving || showSuccessModal) {
            return; // no observar en estos estados
        }

        // Inicializar contador global
        try { window.__SANITIZER_PRUNED_ADS = window.__SANITIZER_PRUNED_ADS || 0; } catch (_) {}

        // Selección de elementos considerados anuncios (solo match directo sobre el elemento)
        const AD_SELECTOR = 'ins.adsbygoogle, [id^="google_ads"], [id^="aswift_"], iframe[src*="googleads"], iframe[id^="google_ads"]';

        const isAdElement = (el) => {
            try {
                if (!el || el.nodeType !== 1) return false; // Element
                return el.matches?.(AD_SELECTOR) === true;
            } catch (_) {
                return false;
            }
        };

        const safeHide = (node) => {
            try {
                if (!node) return false;
                if (!(node instanceof Element)) return false;
                if (node.dataset && node.dataset.adSanitized === '1') return false;
                node.setAttribute('data-ad-sanitized', '1');
                node.setAttribute('aria-hidden', 'true');
                node.setAttribute('hidden', 'true');
                // Aplicar estilos de ocultamiento sin quitar del DOM
                const styleStr = 'display: none !important; visibility: hidden !important; pointer-events: none !important;';
                if (node.style) {
                    node.style.setProperty('display', 'none', 'important');
                    node.style.setProperty('visibility', 'hidden', 'important');
                    node.style.setProperty('pointer-events', 'none', 'important');
                } else {
                    node.setAttribute('style', styleStr);
                }
                return true;
            } catch (_) {
                return false;
            }
        };

        const pruneAds = () => {
            let removed = 0;
            try {
                // Solo ocultar los nodos que matchean directamente el selector
                root.querySelectorAll?.(AD_SELECTOR).forEach(n => { if (safeHide(n)) removed++; });
            } catch (_) {}
            if (removed) try { window.__SANITIZER_PRUNED_ADS = (window.__SANITIZER_PRUNED_ADS || 0) + removed; } catch (_) {}
        };

        // Limpieza inicial
        pruneAds();

        const observer = new MutationObserver((mutations) => {
            let removed = 0;
            for (const m of mutations) {
                const added = m.addedNodes ? Array.from(m.addedNodes) : [];
                for (const node of added) {
                    if (!(node instanceof Element)) continue;
                    // Si el nodo agregado es un anuncio, ocultarlo directamente
                    if (isAdElement(node)) {
                        if (safeHide(node)) removed++;
                        continue;
                    }
                    // Si tiene descendientes que son anuncios, ocultar SOLO esos descendientes
                    try {
                        const targets = node.querySelectorAll?.(AD_SELECTOR) || [];
                        for (const n of Array.from(targets)) {
                            if (safeHide(n)) removed++;
                        }
                    } catch (_) {}
                }
            }
            if (removed) try { window.__SANITIZER_PRUNED_ADS = (window.__SANITIZER_PRUNED_ADS || 0) + removed; } catch (_) {}
        });

        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [saving, showSuccessModal]);


    if (loading) {
        return (
            <div className="edit-provider-loading">
                <Spinner />
                <p>Cargando datos para registro...</p>
            </div>
        );
    }

    // Meta tags dinámicos basados en el tipo de proveedor
    const providerTypeText = providerType === 'service' ? 'Servicios' : 'Productos';
    const pageTitle = `Registrar Perfil de ${providerTypeText} | ContactaMe`;
    const pageDescription = `Creá tu perfil profesional de ${providerTypeText.toLowerCase()} en ContactaMe. Completá tu información, agregá fotos y comenzá a recibir clientes.`;

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta
                    name="keywords"
                    content={`registrar ${providerTypeText.toLowerCase()}, crear perfil proveedor, alta proveedor, contactame, perfil profesional`}
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://contactame.com.ar/registro/${providerType === 'service' ? 'servicios' : 'productos'}`} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content={`https://contactame.com.ar/registro/${providerType === 'service' ? 'servicios' : 'productos'}`} />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href={`https://contactame.com.ar/registro/${providerType === 'service' ? 'servicios' : 'productos'}`} />

                {/* Robots - no indexar páginas de registro */}
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* Overlay para bloquear interacciones durante el scroll */}
            {isScrolling && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'transparent',
                        zIndex: 9999,
                        cursor: 'wait'
                    }}
                    aria-label="Actualizando contenido, por favor espera"
                />
            )}

            <div ref={containerRef} className="edit-provider-modern" data-provider-type={providerType}>
                <div className="edit-header">
                    <div className="edit-header-content">
                        <h1>Crear Perfil</h1>
                        <div className="edit-header-info">
                            <span className="provider-type">{providerType === 'service' ? 'Servicios' : 'Productos'}</span>
                            <span className="provider-username">@{formData.username || 'usuario'}</span>
                        </div>
                    </div>
                    <ProgressIndicator progress={profileProgress} hasUnsavedChanges={hasUnsavedChanges} />
                </div>

                <div className="edit-content">
                    <div className="edit-sidebar">
                        <StepNavigation
                            steps={STEPS}
                            currentStep={currentStep}
                            onStepClick={handleStepClick}
                            errors={errors}
                            formData={formData}
                            disabled={isScrolling}
                        />
                    </div>

                    <div className="edit-main">
                        <div className="step-content">
                            {renderCurrentStep}
                        </div>

                        <div className="step-navigation">
                            <button
                                onClick={handlePrevStep}
                                disabled={currentStep === 0 || isScrolling || saving}
                                className="btn btn-secondary"
                            >
                                ← Anterior
                            </button>
                            <div className="step-indicator">{currentStep + 1} de {STEPS.length}</div>
                            {currentStep < STEPS.length - 1 ? (
                                <button
                                    onClick={handleNextStep}
                                    disabled={isScrolling || saving}
                                    className="btn btn-primary"
                                    style={{ backgroundColor: 'var(--primary-color)' }}
                                >
                                    Siguiente →
                                </button>
                            ) : (
                                <button
                                    onClick={() => !isScrolling && !saving && navigate('/')}
                                    disabled={isScrolling || saving}
                                    className="btn btn-outline"
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showSuccessModal && (
                <RegistrationSuccess onClose={handleCloseSuccessModal} />
            )}
        </>
    );
};

export default RegisterProviderProfile;
